import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { reverseGeocode } from './addressApis'
import { ScreenHeader } from './Components/ScreenHeader'
import { SpinnerIcon, LocationMarkerIcon } from '@/components/ui/icons'
import { useAddressTransform } from './hooks/useAddressTransform'
import type { DeliveryAddress } from '@/types'

const getMapTilerStyleUrl = (): string => {
    if (typeof window !== 'undefined' && window.AppConfig?.api?.mapTilerStyleUrl) {
        return window.AppConfig.api.mapTilerStyleUrl
    }
    return 'https://api.maptiler.com/maps/c009d368-07c5-496e-8047-e69432e7af2b/style.json?key=Kaj7hj4spq6OTB0wtwjG'
}

export function ConfirmLocationScreen() {
    const location = useLocation()
    const navigate = useNavigate()
    const { lat, lng } = location.state || { lat: -37.8136, lng: 144.9631 }
    
    const mapContainerRef = useRef<HTMLDivElement>(null)
    const mapRef = useRef<maplibregl.Map | null>(null)
    const [debouncedCenter, setDebouncedCenter] = useState({ lat, lng })
    const [isMovingMap, setIsMovingMap] = useState(false)
    const [isConfirming, setIsConfirming] = useState(false)
    const [confirmedDetails, setConfirmedDetails] = useState<any>(null)
    const processedDeliveryAddress = useAddressTransform(confirmedDetails)

    useEffect(() => {
        if (processedDeliveryAddress && isConfirming) {
            navigate('/delivery-results', { state: { deliveryAddress: processedDeliveryAddress } })
            setIsConfirming(false)
        }
    }, [processedDeliveryAddress, isConfirming, navigate])

    // Removed redundant useEffect since moveend manages the exact debounce directly

    // Query MapTiler strictly using the debounced coordinates
    const { data: geocodeDetails, isFetching } = useQuery({
        queryKey: ['reverseGeocode', debouncedCenter.lat.toFixed(5), debouncedCenter.lng.toFixed(5)],
        queryFn: () => reverseGeocode(debouncedCenter.lat, debouncedCenter.lng),
        staleTime: 1000 * 60 * 60, // Cache exact coordinates for 1 hour
    })

    // Removed anti-pattern state-syncing useEffects. Label is now entirely declarative.

    useEffect(() => {
        if (!mapContainerRef.current || mapRef.current) return

        const map = new maplibregl.Map({
            container: mapContainerRef.current,
            style: getMapTilerStyleUrl(),
            center: [lng, lat],
            zoom: 16,
            attributionControl: false,
        })

        map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
        map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right')

        map.on('load', () => {
            map.resize()
        })

        map.on('movestart', () => {
            setIsMovingMap(true)
        })

        let debounceTimer: ReturnType<typeof setTimeout>
        map.on('moveend', () => {
            clearTimeout(debounceTimer)
            debounceTimer = setTimeout(() => {
                const center = map.getCenter()
                setDebouncedCenter({ lat: center.lat, lng: center.lng })
                setIsMovingMap(false)
            }, 1000)
        })

        mapRef.current = map

        return () => {
            map.remove()
            mapRef.current = null
        }
    }, [lat, lng])

    const handleConfirm = async () => {
        setIsConfirming(true)
        try {
            const finalCenter = mapRef.current?.getCenter() || { lat, lng }
            const details = await reverseGeocode(finalCenter.lat, finalCenter.lng)
            if (details) {
                setConfirmedDetails(details)
                // useEffect handles navigation once processedDeliveryAddress resolves
            } else {
                const finalCenter = mapRef.current?.getCenter() || { lat, lng }
                const fallbackAddress: DeliveryAddress = {
                    formatted: `${finalCenter.lat.toFixed(6)}, ${finalCenter.lng.toFixed(6)}`,
                    street: 'Selected Location',
                    suburb: 'Near you',
                    state: '',
                    postcode: '',
                    latitude: finalCenter.lat,
                    longitude: finalCenter.lng,
                }
                navigate('/delivery-results', { state: { deliveryAddress: fallbackAddress } })
                setIsConfirming(false)
            }
        } catch (error) {
            console.error('Error confirming location:', error)
            const finalCenter = mapRef.current?.getCenter() || { lat, lng }
            navigate('/delivery-results', { 
                state: { 
                    deliveryAddress: {
                        formatted: `${finalCenter.lat.toFixed(6)}, ${finalCenter.lng.toFixed(6)}`,
                        street: 'Selected Location',
                        suburb: '',
                        state: '',
                        postcode: '',
                        latitude: finalCenter.lat,
                        longitude: finalCenter.lng,
                    }
                } 
            })
            setIsConfirming(false)
        }
    }

    return (
        <div className="h-dvh flex flex-col bg-surface-secondary relative overflow-hidden">
            <div className="bg-surface-primary shadow-sm z-10 relative shrink-0">
                <ScreenHeader
                    title="Confirm Address"
                    showBack
                    onBack={() => navigate(-1)}
                />
            </div>

            <div className="flex-1 relative min-h-[50vh] w-full border-t border-border-light bg-surface-tertiary">
                <div ref={mapContainerRef} className="absolute inset-0 w-full h-full" />
                
                {/* Center Pin Marker */}
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center -mt-8 z-10">
                    <div className="relative">
                        <svg viewBox="0 0 24 30" className="w-10 h-10 text-brand-primary drop-shadow-md">
                            <path 
                                d="M12 0C5.373 0 0 5.373 0 12c0 9 12 18 12 18s12-9 12-18c0-6.627-5.373-12-12-12z" 
                                fill="currentColor"
                            />
                            <circle cx="12" cy="12" r="5" fill="white" />
                        </svg>
                    </div>
                </div>
            </div>

            <div className="p-4 bg-surface-primary border-t border-border-light shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-20">
                <div className="mb-4">
                    <div className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-1">
                        Selected Address
                    </div>
                    <div className="flex items-start gap-2 min-h-[48px]">
                        <LocationMarkerIcon className="size-5 text-brand-primary mt-0.5 shrink-0" />
                        <span className="font-medium text-text-primary text-base line-clamp-2">
                            {isMovingMap ? 'Move map to refine location' : 
                             isFetching ? 'Finding your address...' : 
                             geocodeDetails?.label || 'Unknown location'}
                        </span>
                    </div>
                </div>
                
                <button
                    onClick={handleConfirm}
                    disabled={isConfirming}
                    className="
                        w-full h-13 px-6 text-base
                        bg-brand-primary text-white
                        rounded-xl font-semibold
                        hover:opacity-90
                        active:scale-[0.98]
                        transition-all duration-200
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2
                        disabled:opacity-50 disabled:cursor-not-allowed
                        flex items-center justify-center gap-2 shadow-sm
                    "
                >
                    {isConfirming ? (
                        <>
                            <SpinnerIcon className="size-5" />
                            Confirming...
                        </>
                    ) : (
                        'Confirm Location'
                    )}
                </button>
            </div>
        </div>
    )
}
